/**
 * client/src/components/layout/more-sheet.tsx
 *
 * Mobile "More" bottom sheet — opened from the last bottom tab (☰). Renders
 * the profile block: user name + security level, org switcher, Sign Out.
 * Every nav item itself is a direct bottom tab (see bottom-tabs.tsx's
 * universal parity rule) — this sheet has no overflow-nav role.
 *
 * (Was profile-sheet.tsx / ProfileSheet — renamed when the bottom nav was
 * unified with the desktop sidebar; briefly took on an overflow-nav role too,
 * retired once the bottom bar stopped capping direct tabs.)
 */
import { motion } from "framer-motion";
import { Link } from "wouter";
import { LogOut, Settings, ShieldCheck, Sparkles, X } from "lucide-react";
import { OFFICIAL_CHANNELS_PATH } from "@shared/lib/communication-reference";
import { handleLogout } from "@/lib/logout";
import { OrgSwitcher } from "@/components/org-switcher";
import { protectionLevel } from "@/lib/terminology";
import { CHANGELOG_PATH, CHANGELOG_TITLE } from "@/lib/changelog-view";
import { SCRIM_CLASS, HAIRLINE_RING } from "@/components/premium";

const AAL_COLOUR: Record<string, string> = {
  AAL1: "text-text-tertiary",
  AAL2: "text-interactive",
  AAL3: "text-strenn-emerald",
};

/** Static style object hoisted to module scope — avoids reallocating an
 *  identical `{ boxShadow }` object on every MoreSheet render (Reuse-First /
 *  efficiency, mirrors app-shell.tsx's SHELL_AMBIENT_WASH_STYLE pattern). */
const SHEET_STYLE = { boxShadow: HAIRLINE_RING };

interface MoreSheetProps {
  displayName: string;
  aal:         "AAL1" | "AAL2" | "AAL3";
  onClose:     () => void;
}

export function MoreSheet({ displayName, aal, onClose }: MoreSheetProps) {
  // Task 8 (AC-9): renders the full "Level N of 4" label directly — the
  // former `{short} protection` reconstruction (e.g. a Level-2 badge reading
  // "Strong" + " protection") is exactly what the shared label already
  // says, so no longer needed.
  const aalLabel  = protectionLevel(aal).label;
  const aalColour = AAL_COLOUR[aal] ?? AAL_COLOUR["AAL1"];

  return (
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className={`fixed inset-0 z-50 ${SCRIM_CLASS}`}
      />

      {/* Sheet */}
      <motion.div
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        exit={{ y: "100%" }}
        transition={{ type: "spring", stiffness: 400, damping: 32 }}
        className="fixed bottom-0 left-0 right-0 z-50 bg-bg-elevated border-t border-border-default rounded-t-2xl pb-[env(safe-area-inset-bottom)]"
        style={SHEET_STYLE}
      >
        <div className="flex items-center justify-between px-5 pt-4 pb-3">
          <div>
            <p className="text-text-primary text-sm font-semibold">{displayName}</p>
            <p
              className={`text-xs font-medium ${aalColour}`}
              aria-label={`Protection level: ${aalLabel}`}
            >
              {aalLabel}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-[var(--radius-sm)] text-text-tertiary hover:text-text-primary hover:bg-bg-overlay"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="border-t border-border-subtle mx-4" />

        <div className="px-5 pt-3 space-y-1.5">
          {/* AC-9, #1791 — the "Acting as" label this block used to render is
              now INSIDE the switcher trigger itself, so it appears on every
              surface the switcher is mounted on (desktop sidebar included)
              instead of only here. Keeping both produced a literal
              "ACTING AS / ACTING AS / Personal" stack on this sheet. */}
          <OrgSwitcher openUpward />
        </div>

        <div className="px-5 py-3 space-y-1">
          <Link href="/settings" onClick={onClose} aria-label="Settings">
            <div className="flex items-center gap-3 w-full px-3 py-3 rounded-[var(--radius-sm)] text-text-tertiary hover:text-text-secondary hover:bg-bg-overlay transition-colors">
              <Settings size={20} />
              <span className="text-[length:var(--text-body-size)]">Settings</span>
            </div>
          </Link>
          <Link href={CHANGELOG_PATH} onClick={onClose} aria-label={CHANGELOG_TITLE}>
            <div className="flex items-center gap-3 w-full px-3 py-3 rounded-[var(--radius-sm)] text-text-tertiary hover:text-text-secondary hover:bg-bg-overlay transition-colors">
              <Sparkles size={20} />
              <span className="text-[length:var(--text-body-size)]">{CHANGELOG_TITLE}</span>
            </div>
          </Link>
          {/* Reachability fallback (AC-90): no dedicated Help surface exists
              yet — this shell footer is the fallback link site (mobile). */}
          <Link href={OFFICIAL_CHANNELS_PATH} onClick={onClose} aria-label="Official channels">
            <div className="flex items-center gap-3 w-full px-3 py-3 rounded-[var(--radius-sm)] text-text-tertiary hover:text-text-secondary hover:bg-bg-overlay transition-colors">
              <ShieldCheck size={20} />
              <span className="text-[length:var(--text-body-size)]">Official channels</span>
            </div>
          </Link>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-3 py-3 rounded-[var(--radius-sm)] text-text-tertiary hover:text-text-secondary hover:bg-bg-overlay transition-colors"
            aria-label="Sign out"
          >
            <LogOut size={20} />
            <span className="text-[length:var(--text-body-size)]">Sign out</span>
          </button>
        </div>
      </motion.div>
    </>
  );
}
